import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBookSummaries } from "@/api/books";
import { useSessionState } from "@/hooks/useSessionState";
import { fetchCategories, fetchLanguages } from "@/data/mockData";
import AdminLayout from "@/components/layout/AdminLayout";
import BooksFilter from "@/components/books/BooksFilter";
import { Button } from "@/components/ui/button";
import { BookSummary } from "@/types/index";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import {
  Edit,
  Eye,
  ChevronDown,
  ArrowUpDown,
  Headphones,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Star,
  Bot,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateBookStatus } from "@/api/books";
import { usePagination } from "@/hooks/usePagination";

const BooksList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useSessionState("booksList.searchInput", "");
  const [filters, setFilters] = useSessionState("booksList.filters", {
    category: "",
    access: "all",
    hasAudio: false,
    language: "all",
    contentType: "all",
  });
  // Removed analytics expander state with analytics section

  const [sortColumn, setSortColumn] = useSessionState<
    keyof BookSummary | "created" | "authors" | "languages" | "updatedAt"
  >("booksList.sortColumn", "createdAt");
  const [sortDirection, setSortDirection] = useSessionState<"asc" | "desc">("booksList.sortDirection", "desc");
  const [currentPage, setCurrentPage] = useSessionState("booksList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("booksList.pageSize", 10);

  // Debounce the search input to prevent frequent API calls while typing
  const debouncedSearchTerm = useDebounce(searchInput, 300);

  // Reset to page 1 when the debounced search term changes (but not on initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Analytics query removed from listing page for performance

  // Query for the book listing that will be affected by filters
  const { data: booksData, isLoading: isLoadingBooks } = useQuery({
    queryKey: [
      "books-listing",
      currentPage,
      pageSize,
      debouncedSearchTerm,
      filters,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      fetchBookSummaries({
        page: currentPage,
        pageSize,
        filters: {
          ...filters,
          search: debouncedSearchTerm,
          sortBy: getSortByValue(),
          sortDirection: sortDirection,
        },
      }),
  });

  // Categories and languages data for filtering options
  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: languages, isLoading: isLoadingLanguages } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  // Add mutation for toggling book status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      return updateBookStatus(id, isActive);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books-listing"] });
      toast({
        title: "Status updated",
        description: "Book status has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating status",
        description: "There was a problem updating the book status.",
        variant: "destructive",
      });
    },
  });

  const handleToggleActive = (id: number, isActive: boolean) => {
    // Optimistically update UI
    queryClient.setQueryData(
      [
        "books-listing",
        currentPage,
        pageSize,
        filters,
        sortColumn,
        sortDirection,
      ],
      (oldData: { data: BookSummary[]; total: number } | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          data: oldData.data.map((book: BookSummary) =>
            book.id === id ? { ...book, isActive } : book
          ),
        };
      }
    );

    // Call the API
    toggleStatusMutation.mutate({ id, isActive });
  };

  const handleFilterChange = (name: string, value: string | boolean) => {
    if (name === "search") {
      setSearchInput(value as string);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setCurrentPage(1);
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setFilters({
      category: "",
      access: "all",
      hasAudio: false,
      language: "all",
      contentType: "all",
    });
    setCurrentPage(1);
  };

  const handleSort = (
    column: keyof BookSummary | "created" | "authors" | "languages"
  ) => {
    // First determine if we're toggling an existing sort or setting a new one
    const isToggling = sortColumn === column;
    const newDirection = isToggling
      ? sortDirection === "asc"
        ? "desc"
        : "asc"
      : "desc"; // Default to descending for new column

    // Update the sort column and direction
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  // Helper function to convert sortColumn to sortBy value for API
  const getSortByValue = () => {
    const sortByMapping: Record<string, string> = {
      title: "title",
      authors: "author",
      rating: "rating",
      viewCount: "popularity",
      read_count: "read_count",
      audio_play_count: "audio_play_count",
      languages: "languages",
      isActive: "status",
      created: "newest",
      createdAt: "newest",
      updatedAt: "updated",
    };

    // Special case for created/createdAt with direction
    if (sortColumn === "created" || sortColumn === "createdAt") {
      return sortDirection === "asc" ? "oldest" : "newest";
    }

    // For other columns, use the mapping
    return sortByMapping[sortColumn as string] || "newest";
  };

  // Display loading state for the content within the layout
  // Split the loading state to only show a loading indicator for the book list when filtering
  const isListingLoading = isLoadingBooks;
  const isFiltersLoading =
    isLoadingCategories || isLoadingLanguages || !categories || !languages;

  // Removed analytics computations from listing

  // Calculate total pages for pagination based on filtered data
  const totalFilteredBooks = booksData?.total || 0;

  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: totalFilteredBooks,
    itemsPerPage: pageSize,
    currentPage,
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Comment out local sorting since we're delegating this to the API
  // Sort books
  const sortedBooks = booksData?.data || [];

  // Removed extra analytics metrics

  return (
    <AdminLayout title="Books">
      {/* Analytics Overview moved to Content Analytics Dashboard */}

      {/* Filters Section - Only show when filter options are loaded */}
      {isFiltersLoading ? (
        <div className="flex items-center justify-center h-24 mb-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <Card className="mb-6 bg-card text-card-foreground border-border">
          <CardContent className="py-4">
            <BooksFilter
              filters={filters}
              searchInput={searchInput}
              categories={categories}
              languages={languages}
              onFilterChange={handleFilterChange}
              onResetFilters={resetFilters}
            />
          </CardContent>
        </Card>
      )}

      {/* Books Listing Section - Shows loading state when filters change */}
      <Card className="bg-card text-card-foreground border-border">
        {isListingLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : !booksData ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-muted-foreground">No data available</p>
          </div>
        ) : (
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50">
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Title</span>
                      {sortColumn === "title" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "cursor-pointer hover:bg-muted/50 transition-colors",
                      sortColumn === "authors" && "bg-muted"
                    )}
                    onClick={() => handleSort("authors")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Author</span>
                      {sortColumn === "authors" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("languages")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Editions</span>
                      {sortColumn === "languages" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("rating")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Rating</span>
                      {sortColumn === "rating" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("viewCount")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Views</span>
                      {sortColumn === "viewCount" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("read_count")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Reads</span>
                      {sortColumn === "read_count" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("audio_play_count")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Plays</span>
                      {sortColumn === "audio_play_count" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer w-[60px]"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortColumn === "createdAt" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer w-[60px]"
                    onClick={() => handleSort("updatedAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Updated</span>
                      {sortColumn === "updatedAt" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center w-[100px]"
                    onClick={() => handleSort("isActive")}
                  >
                    <div className="flex items-center justify-center">
                      <Eye className="h-4 w-4 mr-1" />
                      <span>Status</span>
                      {sortColumn === "isActive" && (
                        <ArrowUpDown
                          className={`h-4 w-4 ml-1 ${
                            sortDirection === "asc"
                              ? "transform rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBooks.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="h-24 text-center text-muted-foreground"
                    >
                      No books found.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedBooks.map((book) => (
                    <TableRow
                      key={book.id}
                      className={`hover:bg-muted/50 cursor-pointer ${
                        !book.isActive ? "opacity-60" : ""
                      }`}
                      onClick={() => navigate(`/admin/books/${book.id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="h-16 w-12 overflow-hidden rounded-md border border-border flex-shrink-0 relative"
                            style={{
                              backgroundColor: "#1F2532",
                            }}
                          >
                            {/* Dynamic background color overlay with 80% transparency */}
                            <div
                              className="absolute inset-0"
                              style={{
                                backgroundColor: book.coverBgColor || "#E9ECEF",
                                opacity: 0.8,
                              }}
                            />
                            {book.coverImage ? (
                              <div className="absolute inset-0 flex items-center justify-center p-1 z-10">
                                <img
                                  src={book.coverImage}
                                  alt={book.title}
                                  className="max-h-full max-w-full object-contain"
                                />
                              </div>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground z-10">
                                <div className="flex flex-col items-center justify-center">
                                  <BookOpen className="h-5 w-5 mb-1" />
                                  {book.title?.substring(0, 1).toUpperCase() ||
                                    "?"}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {book.title}
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
                              {book.readingTime} min
                              {book.isFree && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded leading-none">
                                  Free
                                </span>
                              )}
                              {book.embeddingCount && book.embeddingCount > 0 ? (
                                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded flex items-center gap-0.5 leading-none">
                                  <Bot className="h-3 w-3" />
                                  AI
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {book.authors && book.authors.length > 0
                          ? book.authors
                              .map((author) =>
                                author && author.name ? author.name : "Unknown"
                              )
                              .join(", ")
                          : "Unknown"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {book.categories &&
                            book.categories.slice(0, 2).map((category) => {
                              if (!category) return null;

                              return (
                                <Badge
                                  key={category.id}
                                  variant="outline"
                                  className="text-xs border-border"
                                >
                                  {category.name || "Unknown"}
                                </Badge>
                              );
                            })}
                          {book.categories && book.categories.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-xs border-border"
                            >
                              +{book.categories.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {book.summaryContents &&
                          book.summaryContents.length > 0 ? (
                            book.summaryContents.map((content) => {
                              if (!content || !content.language) return null;

                              const language = languages?.find(
                                (lang) => lang.code === content.language
                              );
                              const languageName =
                                language?.name || content.language;
                              const isFullBook = content.contentType === "full_book";
                              const hasAudio = !!content.audioUrl;
                              const chaptersCount =
                                content.chapters?.length || 0;

                              return (
                                <Badge
                                  key={`${content.language}-${content.contentType || 'summary'}`}
                                  className="bg-secondary text-secondary-foreground flex items-center"
                                  title={`${languageName} — ${isFullBook ? "Full Book" : "Summary"}${
                                    hasAudio ? " (with audio)" : ""
                                  }${
                                    chaptersCount
                                      ? ` - ${chaptersCount} chapters`
                                      : ""
                                  }`}
                                >
                                  {isFullBook ? (
                                    <BookOpen className="h-3 w-3 mr-1 text-violet-600 dark:text-violet-400" />
                                  ) : (
                                    <Zap className="h-3 w-3 mr-1 text-amber-500" />
                                  )}
                                  {content.language.toUpperCase()}
                                  {chaptersCount > 0 && (
                                    <div className="ml-1.5 flex items-center gap-0.5 bg-primary/10 px-1 rounded-sm text-primary">
                                      <span className="text-[10px] font-medium">
                                        {chaptersCount}
                                      </span>
                                    </div>
                                  )}
                                  {hasAudio && (
                                    <Headphones className="h-3 w-3 ml-1" />
                                  )}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              No content
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {book.rating && book.rating > 0 ? (
                            <>
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                              <span className="font-medium">
                                {book.rating.toFixed(1)}
                              </span>
                              {book.ratingCount && book.ratingCount > 0 ? (
                                <span className="text-xs text-muted-foreground">
                                  ({book.ratingCount})
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              No ratings
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Eye className="mr-2 h-4 w-4 text-muted-foreground" />
                          {book.viewCount.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" />
                          {(book.read_count || 0).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Headphones className="mr-2 h-4 w-4 text-muted-foreground" />
                          {(book.audio_play_count || 0).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="whitespace-nowrap">
                            {new Date(book.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(book.createdAt).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="whitespace-nowrap">
                            {new Date(book.updatedAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(book.updatedAt).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={book.isActive}
                            onCheckedChange={(checked) => {
                              handleToggleActive(book.id, checked);
                            }}
                            className="data-[state=checked]:bg-green-500"
                            aria-label={
                              book.isActive
                                ? "Book is active"
                                : "Book is hidden"
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="bg-popover border-border"
                            >
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/admin/books/${book.id}/edit`);
                                }}
                                className="text-foreground cursor-pointer"
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Book
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium">
                  {totalFilteredBooks === 0
                    ? 0
                    : (currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalFilteredBooks)}
                </span>{" "}
                of <span className="font-medium">{totalFilteredBooks}</span>{" "}
                books
              </div>
              <div className="flex items-center gap-4">
                {totalPages > 0 && (
                  <div className="flex items-center">
                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={!canGoPrev}
                    >
                      First
                    </Button>

                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                      }
                      disabled={!canGoPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {pageNumbers.map((pageNum) => (
                      <Button
                        key={pageNum}
                        className="mx-0.5 w-8 h-8 p-0"
                        variant={
                          pageNum === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    ))}

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(
                          currentPage < totalPages
                            ? currentPage + 1
                            : totalPages
                        )
                      }
                      disabled={!canGoNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={!canGoNext}
                    >
                      Last
                    </Button>
                  </div>
                )}

                <select
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  {[10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </Card>
    </AdminLayout>
  );
};

export default BooksList;
